import { chatCompletionAPI } from '@/proxy-request/api'
import { useState, useRef, useEffect } from 'react'
import { getStores } from '@/stores'
import { generateUUID } from '@/utils'
import { observer } from 'mobx-react'
import styles from './index.module.scss'
import { message, Tooltip, Select, Form } from 'antd'
import productVideoIcon from '@/assets/images/tt-logo.svg'
import sendIcon from './send-icon.svg'
import { CloseOutlined, LogoutOutlined } from '@ant-design/icons'
import cn from 'classnames'
import { $t } from '@/i18n/intl'
import productIcon from '@/assets/images/product-icon.svg'
import { stopStreamAPI } from '@/servers/agent-v2'
import { CosUpload } from '@/components/upload'
import useInitHistroy from '@/hooks/use-init-histroy'
import { requestChatScrollToBottomAlways } from '@/pages/main/helpers/request-chat-scroll-to-bottom'

enum SendButtonState {
  normal = 'normal',
  loading = 'loading',
  generating = 'generating'
}

/** Figma node 4140:1889：12×12 细线 chevron（描边 V），对齐设计稿非 DownOutlined 实心三角 */
const countrySelectSuffixIcon = (
  <span className={styles.countrySelectChevron} aria-hidden>
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M2.75 4.5L6 8.25L9.25 4.5"
        stroke="currentColor"
        strokeWidth="1"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </span>
)

export const countryOptions = [
  // {
  //   label: '中国',
  //   value: 'CN'
  // },
  {
    label: '美国',
    value: 'US'
  },
  {
    label: '日本',
    value: 'JP'
  },
  {
    label: '泰国',
    value: 'TH'
  },
  {
    label: '马来',
    value: 'MY'
  },
  {
    label: '越南',
    value: 'VN'
  },
  {
    label: '印尼',
    value: 'ID'
  },
  {
    label: '英国',
    value: 'GB'
  },
  {
    label: '意大利',
    value: 'IT'
  },
  {
    label: '法国',
    value: 'FR'
  },
  {
    label: '西班牙',
    value: 'ES'
  },
  {
    label: '墨西哥',
    value: 'MX'
  },
  {
    label: '德国',
    value: 'DE'
  }
]

const Sender = () => {
  const { agentV2Store } = getStores()
  const { initHistory } = useInitHistroy({ autofetch: false })
  const [value, setValue] = useState<string>('')
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const [selectedQuickActionIndex, setSelectedQuickActionIndex] = useState<number>(0)
  const [productImageUrls, setProductImageUrls] = useState<string[]>([])
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null)
  const [countryRequiredError, setCountryRequiredError] = useState(false)
  const isProductImageMode = selectedQuickActionIndex === 1

  const placeholderText = isProductImageMode
    ? '点击上传商品图，可以简单描述你的商品信息'
    : `请输入或粘贴商品链接到此处，请先选择左侧的商品地区并与商品链接地区保持一致，否则链接会解析失败（例如https://shop.tiktok.com/product/123）`

  useEffect(() => {
    const textarea = textareaRef.current
    if (textarea) {
      textarea.style.height = 'auto'
      const scrollHeight = textarea.scrollHeight
      const lineHeight = parseFloat(getComputedStyle(textarea).lineHeight) || 20
      const minHeight = isProductImageMode ? lineHeight * 1.5 : lineHeight * 3
      const maxHeight = lineHeight * 6
      const newHeight = Math.min(Math.max(scrollHeight, minHeight), maxHeight)
      textarea.style.height = `${newHeight}px`
    }
  }, [isProductImageMode, value])

  useEffect(() => {
    if (isProductImageMode) {
      setCountryRequiredError(false)
    }
    agentV2Store.isProductImageMode = isProductImageMode
  }, [agentV2Store, isProductImageMode])

  const sendMsg = (text: string) => {
    if (agentV2Store.isHistoryChatMode) {
      message.warning($t('demo.sender.pleaseExitHistoryMode'))
      return
    }

    if (!isProductImageMode && (selectedCountry == null || selectedCountry === '')) {
      message.warning('请先选择商品地区')
      setCountryRequiredError(true)
      return
    }

    if (isProductImageMode && productImageUrls.length === 0) {
      message.warning('请先上传商品图')
      setLoading(false)
      return
    }
    if (isProductImageMode && text.trim() === '') {
      message.warning('请描述商品信息')
      return
    }

    if (text.trim() === '' && !isProductImageMode) {
      message.warning('请输入商品链接')
      return
    }

    if (isProductImageMode) {
      agentV2Store.chatLoadingState.loading = true
      agentV2Store.renderMsgList.push({
        id: generateUUID(),
        message_id: generateUUID(),
        sender: 'user',
        placement: 'end',
        content: [
          {
            type: 'block',
            typing: true,
            value: [
              {
                type: 'image_with_text',
                value: {
                  images: productImageUrls,
                  text: text
                }
              }
            ],
            showAvatar: false
          }
        ]
      })
    } else {
      agentV2Store.chatLoadingState.loading = true
      agentV2Store.renderMsgList.push({
        id: generateUUID(),
        message_id: generateUUID(),
        sender: 'user',
        placement: 'end',
        content: [
          {
            type: 'block',
            typing: true,
            value: [{ type: 'text', value: text }],
            showAvatar: false
          }
        ]
      })
    }

    requestChatScrollToBottomAlways()
    setLoading(true)

    const body = isProductImageMode
      ? {
          images: productImageUrls,
          content: value,
          intention: quickActions[selectedQuickActionIndex].intention
        }
      : {
          content: value,
          country: selectedCountry,
          intention: quickActions[selectedQuickActionIndex].intention
        }

    chatCompletionAPI({
      body: body,
      opt: {
        renderToChatPanel: true,
        requestType: 'sse',
        onConnect: () => {
          setLoading(false)
          setValue('')
          setProductImageUrls([])
        },
        onBusinessError() {
          setLoading(false)
        },
        onDisconnect: () => {
          agentV2Store.chatLoadingState.loading = false
        },
        onError() {
          setLoading(false)
        }
      }
    })
  }

  const sendBtnDisabled =
    (isProductImageMode
      ? productImageUrls.length === 0 || value.trim() === ''
      : value.trim() === '') ||
    agentV2Store.chatLoadingState.loading ||
    agentV2Store.isHistoryChatMode

  const sendButtonState: 'normal' | 'loading' | 'generating' = loading
    ? SendButtonState.loading
    : agentV2Store.typing
    ? SendButtonState.generating
    : SendButtonState.normal

  const disabled = agentV2Store.typing || agentV2Store.chatLoadingState.loading

  // if (agentV2Store.inIframePreview || agentV2Store.showGuideEntry) {
  //   return null
  // }

  const quickActions = [
    { label: '链接生成广告素材', icon: productVideoIcon, intention: 'product_link_generation' },
    { label: '商品图片生成广告素材', icon: productIcon, intention: 'product_image_generation' }
  ]

  // 历史记录浏览模式：只展示退出按钮，输入框/下拉/快捷按钮均不展示
  if (agentV2Store.isHistoryChatMode) {
    return (
      <div className={styles.container}>
        <div className={styles.exitHistoryCenter}>
          <Tooltip title="点击即可退出浏览历史消息模式" placement="top">
            <div
              className={styles.exitHistoryWrap}
              onClick={async () => {
                await initHistory()
                agentV2Store.isHistoryChatMode = false
                message.info('已退出浏览历史消息模式')
              }}
            >
              <LogoutOutlined className={styles.exitHistoryIcon} />
            </div>
          </Tooltip>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.container}>
      <div className={cn(styles.inputWrapper, { [styles.disabled]: disabled })}>
        <div
          className={cn(styles.inputArea, { [styles.productImageInputArea]: isProductImageMode })}
        >
          {isProductImageMode ? (
            <div className={styles.uploadSlot}>
              <CosUpload
                disabled={disabled}
                value={productImageUrls}
                multiple
                maxCount={9}
                useDraggerUpload={false}
                onChange={val => setProductImageUrls(Array.isArray(val) ? val : val ? [val] : [])}
                placeholder="上传商品图片"
                description="JPG / PNG，最多 9 张"
              ></CosUpload>
            </div>
          ) : (
            <div className={styles.inputLeadingSlot}>
              <Form.Item
                className={styles.countrySelectFormItem}
                validateStatus={countryRequiredError ? 'error' : undefined}
              >
                <Select
                  disabled={disabled}
                  className={styles.countrySelect}
                  popupClassName={styles.countrySelectDropdown}
                  onChange={v => {
                    setSelectedCountry(v ?? null)
                    setCountryRequiredError(false)
                  }}
                  value={selectedCountry}
                  placeholder="商品地区"
                  options={countryOptions}
                  popupMatchSelectWidth={false}
                  suffixIcon={countrySelectSuffixIcon}
                />
              </Form.Item>
            </div>
          )}
          <div className={styles.textAreaWrap}>
            <textarea
              ref={textareaRef}
              className={cn(styles.textArea, {
                [styles.textAreaWithClear]: Boolean(value.trim()) && !disabled
              })}
              disabled={disabled}
              placeholder={placeholderText}
              value={value}
              rows={isProductImageMode ? 1 : 3}
              onChange={e => setValue(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  if (!sendBtnDisabled && !disabled) {
                    e.preventDefault()
                    sendMsg(value.trim())
                  }
                }
              }}
            />
            {value.trim() && !disabled && (
              <button
                className={styles.clearButton}
                onClick={() => {
                  setValue('')
                  textareaRef.current?.focus()
                }}
                type="button"
                aria-label={$t('demo.sender.clearInput')}
              >
                <CloseOutlined />
              </button>
            )}
          </div>
        </div>
        <div className={styles.actionArea}>
          <div className={styles.quickActions}>
            {quickActions.map((action, index) => {
              const isDisabled = disabled
              sendButtonState === SendButtonState.loading ||
                sendButtonState === SendButtonState.generating
              const isSelected = index === selectedQuickActionIndex

              return (
                <div
                  key={index}
                  className={cn(styles.quickActionBtn, {
                    [styles.disabled]: isDisabled,
                    [styles.selected]: isSelected
                  })}
                  onClick={() => {
                    if (isDisabled) return
                    setSelectedQuickActionIndex(index)
                    if (index === 1) {
                      setValue('')
                    }
                    if (index === 0) {
                      setValue('')
                      setProductImageUrls([])
                    }
                  }}
                >
                  {action.icon && <img src={action.icon} alt="" className={styles.actionIcon} />}
                  <span>{action.label}</span>
                </div>
              )
            })}
          </div>
          <Tooltip
            title={
              sendButtonState === SendButtonState.generating
                ? $t('demo.sender.generating')
                : undefined
            }
          >
            <div
              className={cn(styles.sendButton, {
                [styles.disabled]: sendBtnDisabled || sendButtonState === SendButtonState.loading
              })}
              onClick={() => {
                if (sendButtonState === SendButtonState.generating) {
                  stopStreamAPI({
                    event_id: agentV2Store.lastEventId
                  })
                  return
                }
                if (sendButtonState === SendButtonState.normal && !sendBtnDisabled && !disabled) {
                  sendMsg(value.trim())
                }
              }}
            >
              {sendButtonState === SendButtonState.normal && <img src={sendIcon} alt="send" />}
              {sendButtonState === SendButtonState.loading && (
                <div className={styles.loadingSpinner}></div>
              )}
              {sendButtonState === SendButtonState.generating && (
                <div className={styles.generatingIcon}></div>
              )}
            </div>
          </Tooltip>
        </div>
      </div>
    </div>
  )
}

export default observer(Sender)
